import { BaseEdge, EdgeLabelRenderer, getBezierPath } from '@xyflow/react';
import { edgeStrokeStyle } from 'components/workflow/generic/flowkit/flowEdge';

export { edgeStrokeStyle } from 'components/workflow/generic/flowkit/flowEdge';

/**
 * missionEdge.js — handle-anchored depends_on edge for the mission dependency graph.
 *
 * The edge SNAPS to the cells' connection handles rather than floating against their borders: each edge
 * object (built in missionGraph.js) sets sourceHandle = the dependent's LEFT handle and targetHandle =
 * the prerequisite's RIGHT handle, so React Flow resolves those handle anchor points and hands this
 * component the ready-made endpoints (sourceX/Y, targetX/Y) and the handle sides (sourcePosition,
 * targetPosition). We feed them straight to getBezierPath — no node-border intersection, no re-measuring.
 *
 * Direction: source = from_id (the dependent), target = to_id (the prerequisite), so the bezier leaves
 * the dependent's LEFT handle and the arrowhead lands ON the prerequisite's RIGHT handle, reading
 * "dependent depends on prerequisite". The arrowhead marker (defined in components/graphCanvas.js)
 * recolours with the edge via its selected variant.
 */

function MissionEdge({
  sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition, selected, data,
}) {
  const stroke = edgeStrokeStyle(selected);
  const markerEnd = `url(#${selected ? 'mc-graph-edge-arrow-selected' : 'mc-graph-edge-arrow'})`;
  const label = data && data.label;

  // Bezier curve between the two handle-anchored endpoints. The source/target Positions come straight
  // from the resolved handles (Left for the dependent, Right for the prerequisite), so the curve leaves
  // and enters each handle's facing side.
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  return (
    <>
      <BaseEdge path={edgePath} markerEnd={markerEnd} style={stroke} />
      {label && (
        <EdgeLabelRenderer>
          {/* Chip label over the curve — matches the Blob Workflow / Graph widget edge labels (the
              surface plate, small padding + radius). The text darkens to the selected accent when the
              edge is selected, so the label highlights together with the stroke. The plate is
              --bh-surface because `.mc-graph-canvas` is transparent over the panel card, so the chip
              knocks the curve out of the card in both themes — the same thing #fff did in light. */}
          <div
            className={`nodrag nopan${selected ? ' selected' : ''}`}
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              fontSize: 11,
              color: selected ? 'var(--bh-edge-selected)' : 'var(--bh-edge)',
              background: 'var(--bh-surface)',
              padding: '0 3px',
              borderRadius: 3,
              pointerEvents: 'all',
              zIndex: selected ? 10 : 1,
            }}
          >
            {label}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}

export default MissionEdge;
